import React, { useState, useLayoutEffect, useRef, CSSProperties } from "react";
import styles from "./CollapsibleMessage.module.scss";
import Locale from "@/app/locales";

interface CollapsibleMessageProps {
  children: React.ReactNode;
  maxHeight?: number; // 最大高度，单位像素
  expandText?: string; // 展开按钮文字
  collapseText?: string; // 收起按钮文字
  className?: string; // 自定义容器类名
  gradientColor?: string; // 渐变遮罩颜色
  hideCollapseText?: boolean; // 是否隐藏收起
  useCollapse?: boolean;
}

const CollapsibleMessage: React.FC<CollapsibleMessageProps> = ({
  children,
  maxHeight = 150,
  expandText = Locale.chat.expandText,
  collapseText = Locale.chat.collapseText,
  className = "",
  gradientColor = "rgba(255, 255, 255, 1)",
  hideCollapseText = true,
  useCollapse = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  /** 不使用折叠时视为无限高，避免与 props 入参混写 */
  const effectiveMaxHeight = useCollapse ? maxHeight : Number.MAX_SAFE_INTEGER;

  // 创建CSS变量供CSS模块使用
  const cssVariables: CSSProperties = {
    "--max-height": `${effectiveMaxHeight}px`,
    "--gradient-color": gradientColor,
  } as CSSProperties;

  /**
   * 用 ResizeObserver 监听内容高度，勿把 `children` 放进依赖：
   * 父组件每次 render 常产生新 element 引用，会触发无意义 effect + setState，
   * 与点击展开/遮罩卸载等同帧更新交错时，偶发 React removeChild 与真实 DOM 不一致（Sentry 难复现）。
   */
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const measure = () => {
      const overflowing = el.scrollHeight > effectiveMaxHeight;
      setIsOverflow(overflowing);
    };

    measure();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [effectiveMaxHeight]);

  // 内容不再溢出时收起折叠态，避免状态与布局不一致
  useLayoutEffect(() => {
    if (!isOverflow) {
      setIsExpanded(false);
    }
  }, [isOverflow]);

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const contentClasses =
    isOverflow && !isExpanded
      ? styles.contentCollapsed
      : styles.contentExpanded;

  return (
    <div className={`${styles.container} ${className}`} style={cssVariables}>
      <div className={contentClasses} ref={contentRef}>
        {children}
      </div>

      {isOverflow && !isExpanded && <div className={styles.mask} />}

      {isOverflow && (
        <button
          onClick={toggleExpand}
          className={styles.toggleButton}
          aria-label={isExpanded ? collapseText : expandText}
        >
          {isExpanded ? (
            hideCollapseText ? null : (
              <>
                {collapseText}
                <svg
                  className={styles.icon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 15l7-7 7 7"
                  ></path>
                </svg>
              </>
            )
          ) : (
            <>
              {expandText}
              <svg
                className={styles.icon}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default CollapsibleMessage;
